import { useState, useEffect } from "react";
import { formatFileSize } from "@/utils/formatFileSize";

interface UseFileSizeResult {
  size: number | null;
  formattedSize: string | null;
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook to fetch and format file size from a URL
 * @param url - The file URL to probe
 * @returns Object with size in bytes, formatted size string, loading state, and error
 */
export const useFileSize = (url: string | null): UseFileSizeResult => {
  const [size, setSize] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!url) {
      setSize(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const fetchFileSize = async () => {
      try {
        const response = await fetch(`/api/file-size?url=${encodeURIComponent(url)}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch file size: ${response.status}`);
        }

        const data = await response.json();

        if (data.error) {
          throw new Error(data.error);
        }

        setSize(data.size);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
        setSize(null);
      } finally {
        setLoading(false);
      }
    };

    fetchFileSize();
  }, [url]);

  const formattedSize = formatFileSize(size);

  return {
    size,
    formattedSize,
    loading,
    error,
  };
};
